import { useCallback, useEffect, useState, forwardRef, useImperativeHandle } from 'react';

import { api } from '../api.js';
import { toast } from './Toasts.jsx';
import { refreshNavCounts } from '../hooks/useNavCounts.js';

// T394 — what you did, and a way back.
//
// He asked for this in the same breath as "unele acțiuni revin și revin", and
// the need was concrete rather than theoretical: a CBS confirmation went onto
// an unrelated SameSystem application by hand, and the only way to reverse it
// was me, editing the database. An operator should not need a developer to
// take back his own click.
//
// Deliberately NOT the activity feed. That one answers "what happened while I
// was away" and is mostly scans; four operator actions would be buried under
// forty scan rows. Same collection, different question, different view.

const KIND_ICON = {
  'stage-changed': '→',
  'mail-linked': '⇄',
  'mail-unlinked': '⇠',
  'application-created': '+',
};

function when(at) {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

const TaskHistory = forwardRef(function TaskHistory({ onDone }, ref) {
  const [items, setItems] = useState(null);
  // T434 — "nothing you have done yet" and "I could not read the history"
  // are different sentences, and the second one matters more: it is the
  // page that holds every undo.
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/activity/history?limit=20');
      setItems(r.items ?? []);
      setFailed(false);
    } catch {
      setFailed(true);
      setItems([]);
    }
  }, []);

  useImperativeHandle(ref, () => ({ reload: load }), [load]);
  useEffect(() => {
    load();
  }, [load]);

  const undo = async (row) => {
    setBusy(row.id);
    try {
      await api.post(`/activity/${row.id}/undo`, {});
      toast('Undone.', { kind: 'success' });
      await load();
      // Everything else on this page may now be stale — the proposal that was
      // resolved is open again, the badge is wrong.
      refreshNavCounts();
      onDone?.();
    } catch (e) {
      // The server refuses rather than clobbering when something changed in
      // between, and that refusal carries the reason. Showing it verbatim is
      // the point: "this is now rejected, not applied" tells him what to do.
      toast(e.body?.message || 'Could not undo that.', { kind: 'error' });
    } finally {
      setBusy(null);
    }
  };

  if (failed) {
    return (
      <section className="space-y-2">
        <h2 className="text-sm font-medium text-brand-500">What you did</h2>
        <p className="text-xs text-slate-500">
          Could not load your history just now — this is not the same as having done nothing.
        </p>
      </section>
    );
  }
  if (!items || items.length === 0) return null;

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-medium text-brand-500">What you did</h2>
      <p className="max-w-prose text-xs text-slate-500">
        The last twenty actions you took here. Undo restores exactly what changed — and refuses if
        something else has touched it since, rather than overwriting that.
      </p>
      <ul className="divide-y divide-slate-800 rounded-md border border-slate-800 bg-slate-900/40">
        {items.map((a) => (
          <li key={a.id} className="flex items-start gap-2 px-3 py-2">
            <span aria-hidden="true" className="mt-0.5 w-3 shrink-0 text-center text-xs text-slate-500">
              {KIND_ICON[a.kind] ?? '·'}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-xs text-slate-200">{a.title}</span>
              <span className="mt-0.5 block text-[11px] text-slate-500">
                {when(a.at)}
                {a.undoneAt ? ' · undone' : ''}
              </span>
            </span>
            {a.undoable ? (
              <button
                type="button"
                disabled={busy === a.id}
                onClick={() => undo(a)}
                className="shrink-0 rounded border border-slate-700 px-2 py-0.5 text-[11px] text-slate-300 transition hover:border-brand-600 hover:text-brand-400 disabled:opacity-50"
              >
                {busy === a.id ? '…' : 'Undo'}
              </button>
            ) : (
              <span className="shrink-0 text-[11px] text-slate-600">{a.undoneAt ? '✓' : ''}</span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
});

export default TaskHistory;
